<script setup>
import { ref, onMounted, computed } from 'vue';
import { useToast } from 'primevue/usetoast';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';

import { useMFA } from '@/composables/useMFA';
import { useWebAuthn } from '@/composables/useWebAuthn';
import TOTPEnrollment from '@/components/Auth/TOTPEnrollment.vue';
import PasskeyEnrollment from '@/components/Auth/PasskeyEnrollment.vue';
import RecoveryCodes from '@/components/Account/RecoveryCodes.vue';

const toast = useToast();
const confirm = useConfirm();
const mfa = useMFA();
const webauthn = useWebAuthn();

const isLoading = ref(true);
const mfaStatus = ref({
  totp_enabled: false,
  email_enabled: false,
  passkeys: [],
});

// Dialog state
const showTOTPDialog = ref(false);
const showPasskeyDialog = ref(false);
const showDisableTOTPDialog = ref(false);
const showRecoveryCodesDialog = ref(false);
const disableTOTPCode = ref('');
const isDisabling = ref(false);

const hasTOTP = computed(() => mfaStatus.value.totp_enabled);
const hasPasskeys = computed(() => mfaStatus.value.passkeys?.length > 0);
const passkeyCount = computed(() => mfaStatus.value.passkeys?.length || 0);

const fetchMFAStatus = async () => {
  isLoading.value = true;
  try {
    const status = await mfa.get2FAStatus();
    mfaStatus.value = status;
  } catch (error) {
    console.error('Failed to fetch MFA status:', error);
  } finally {
    isLoading.value = false;
  }
};

// TOTP handlers
const onTOTPEnrolled = () => {
  showTOTPDialog.value = false;
  mfaStatus.value.totp_enabled = true;
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'Authenticator app has been enabled',
    life: 3000,
  });
};

const confirmDisableTOTP = () => {
  showDisableTOTPDialog.value = true;
  disableTOTPCode.value = '';
};

const disableTOTP = async () => {
  if (disableTOTPCode.value.length !== 6) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please enter a valid 6-digit code',
      life: 3000,
    });
    return;
  }

  isDisabling.value = true;
  try {
    await mfa.disableTOTP(disableTOTPCode.value);
    mfaStatus.value.totp_enabled = false;
    showDisableTOTPDialog.value = false;
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Authenticator app has been disabled',
      life: 3000,
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.message || 'Failed to disable authenticator',
      life: 3000,
    });
  } finally {
    isDisabling.value = false;
  }
};

// Passkey handlers
const onPasskeyEnrolled = () => {
  showPasskeyDialog.value = false;
  fetchMFAStatus(); // Refresh to get new passkey
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'Passkey has been added',
    life: 3000,
  });
};

const confirmDeletePasskey = (passkey) => {
  confirm.require({
    message: `Are you sure you want to remove the passkey "${passkey.name}"?`,
    header: 'Remove Passkey',
    icon: 'fas fa-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await mfa.deletePasskey(passkey.id);
        mfaStatus.value.passkeys = mfaStatus.value.passkeys.filter((p) => p.id !== passkey.id);
        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Passkey has been removed',
          life: 3000,
        });
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to remove passkey',
          life: 3000,
        });
      }
    },
  });
};

const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

onMounted(() => {
  fetchMFAStatus();
});
</script>

<template>
  <div class="security-settings">
    <ConfirmDialog />

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner">
        <i class="fas fa-circle-notch fa-spin"></i>
      </div>
      <p>Loading security settings...</p>
    </div>

    <div v-else>
      <!-- Two-Factor Authentication Section -->
      <div class="security-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="fas fa-shield-alt"></i>
          </div>
          <div class="section-info">
            <h4>Two-Factor Authentication</h4>
            <p>Add an extra layer of security to your account</p>
          </div>
        </div>

        <!-- Authenticator App (TOTP) -->
        <div class="security-method">
          <div class="method-info">
            <div class="method-icon totp">
              <i class="fas fa-mobile-alt"></i>
            </div>
            <div class="method-details">
              <h5>Authenticator App</h5>
              <p>Use Google Authenticator, Authy, or similar apps</p>
            </div>
          </div>
          <div class="method-status">
            <span v-if="hasTOTP" class="status-badge enabled">
              <i class="fas fa-check-circle"></i>
              Enabled
            </span>
            <span v-else class="status-badge disabled">
              <i class="fas fa-times-circle"></i>
              Not set up
            </span>
          </div>
          <div class="method-actions">
            <button
              v-if="!hasTOTP"
              class="action-btn primary"
              @click="showTOTPDialog = true"
            >
              <i class="fas fa-plus"></i>
              Set Up
            </button>
            <button
              v-else
              class="action-btn danger"
              @click="confirmDisableTOTP"
            >
              <i class="fas fa-trash-alt"></i>
              Remove
            </button>
          </div>
        </div>

        <!-- Passkeys -->
        <div class="security-method">
          <div class="method-info">
            <div class="method-icon passkey">
              <i class="fas fa-fingerprint"></i>
            </div>
            <div class="method-details">
              <h5>Passkeys</h5>
              <p>Sign in with Face ID, Touch ID, or Windows Hello</p>
            </div>
          </div>
          <div class="method-status">
            <span v-if="hasPasskeys" class="status-badge enabled">
              <i class="fas fa-check-circle"></i>
              {{ passkeyCount }} passkey{{ passkeyCount !== 1 ? 's' : '' }}
            </span>
            <span v-else class="status-badge disabled">
              <i class="fas fa-times-circle"></i>
              Not set up
            </span>
          </div>
          <div class="method-actions">
            <button
              class="action-btn primary"
              @click="showPasskeyDialog = true"
              :disabled="!webauthn.isSupported.value"
            >
              <i class="fas fa-plus"></i>
              Add Passkey
            </button>
          </div>
        </div>

        <!-- Passkey List -->
        <div v-if="hasPasskeys" class="passkey-list">
          <h6><i class="fas fa-key"></i> Your Passkeys</h6>
          <div
            v-for="passkey in mfaStatus.passkeys"
            :key="passkey.id"
            class="passkey-item"
          >
            <div class="passkey-icon">
              <i class="fas fa-key"></i>
            </div>
            <div class="passkey-info">
              <span class="passkey-name">{{ passkey.name }}</span>
              <span class="passkey-meta">
                <i class="fas fa-calendar-plus"></i> Added {{ formatDate(passkey.created_at) }}
                <span v-if="passkey.last_used_at">
                  · <i class="fas fa-clock"></i> Last used {{ formatDate(passkey.last_used_at) }}
                </span>
              </span>
            </div>
            <button
              class="passkey-delete"
              @click="confirmDeletePasskey(passkey)"
              title="Remove passkey"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Recovery Codes Section -->
      <div class="security-section recovery">
        <div class="section-header">
          <div class="section-icon recovery">
            <i class="fas fa-life-ring"></i>
          </div>
          <div class="section-info">
            <h4>Recovery Codes</h4>
            <p>Backup codes for emergency account access</p>
          </div>
        </div>

        <div class="security-method">
          <div class="method-info">
            <div class="method-icon recovery">
              <i class="fas fa-shield-alt"></i>
            </div>
            <div class="method-details">
              <h5>Backup Recovery Codes</h5>
              <p>Use these codes if you lose access to your authenticator</p>
            </div>
          </div>
          <div class="method-actions">
            <button
              class="action-btn secondary"
              @click="showRecoveryCodesDialog = true"
            >
              <i class="fas fa-key"></i>
              Manage Codes
            </button>
          </div>
        </div>
      </div>

      <!-- Browser/Device Not Supported Warning -->
      <div v-if="!webauthn.isSupported.value" class="browser-warning">
        <i class="fas fa-info-circle"></i>
        <span>Passkeys are not supported on this browser. Try using Chrome, Safari, or Edge.</span>
      </div>
    </div>

    <!-- TOTP Enrollment Dialog -->
    <Dialog
      v-model:visible="showTOTPDialog"
      header="Set Up Authenticator"
      :style="{ width: '520px' }"
      modal
      :closable="true"
      :dismissableMask="false"
      class="security-dialog"
    >
      <TOTPEnrollment
        @enrolled="onTOTPEnrolled"
        @cancel="showTOTPDialog = false"
      />
    </Dialog>

    <!-- Passkey Enrollment Dialog -->
    <Dialog
      v-model:visible="showPasskeyDialog"
      header="Add Passkey"
      :style="{ width: '520px' }"
      modal
      :closable="true"
      :dismissableMask="false"
      class="security-dialog"
    >
      <PasskeyEnrollment
        @enrolled="onPasskeyEnrolled"
        @cancel="showPasskeyDialog = false"
      />
    </Dialog>

    <!-- Disable TOTP Dialog -->
    <Dialog
      v-model:visible="showDisableTOTPDialog"
      header="Disable Authenticator"
      :style="{ width: '420px' }"
      modal
      :closable="true"
      class="security-dialog danger"
    >
      <div class="disable-totp-dialog">
        <div class="warning-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <p>Enter the code from your authenticator app to confirm disabling two-factor authentication.</p>
        <input
          v-model="disableTOTPCode"
          type="text"
          inputmode="numeric"
          maxlength="6"
          placeholder="000000"
          class="disable-code-input"
          @keyup.enter="disableTOTP"
        />
        <div class="disable-actions">
          <button class="btn-cancel" @click="showDisableTOTPDialog = false">
            <i class="fas fa-times"></i>
            Cancel
          </button>
          <button
            class="btn-danger"
            @click="disableTOTP"
            :disabled="disableTOTPCode.length !== 6 || isDisabling"
          >
            <i v-if="!isDisabling" class="fas fa-trash-alt"></i>
            <i v-else class="fas fa-circle-notch fa-spin"></i>
            {{ isDisabling ? 'Disabling...' : 'Disable' }}
          </button>
        </div>
      </div>
    </Dialog>

    <!-- Recovery Codes Dialog -->
    <Dialog
      v-model:visible="showRecoveryCodesDialog"
      header="Recovery Codes"
      :style="{ width: '520px' }"
      modal
      :closable="true"
      class="security-dialog"
    >
      <RecoveryCodes @close="showRecoveryCodesDialog = false" />
    </Dialog>
  </div>
</template>

<style scoped>
.security-settings {
  padding: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
}

.loading-spinner {
  font-size: 2rem;
  color: #00d4ff;
}

.loading-state p {
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
}

.security-section {

  padding: 1.5rem;
  margin-bottom: 1.5rem;

  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 16px;
  overflow: hidden;
}

.security-section.recovery {
  border-color: rgba(245, 158, 11, 0.2);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
}

.section-icon {
  width: 48px;
  height: 48px;
  background: rgba(250, 90, 230, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-icon.recovery {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.section-icon i {
  font-size: 1.25rem;
  color: white;
}

.section-info h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
  margin: 0 0 0.25rem;
}

.section-info p {
  font-size: 0.9rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
}

.security-method {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.06));
  border-radius: 12px;
  margin-bottom: 1rem;
}

.security-method:last-child {
  margin-bottom: 0;
}

.method-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.method-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.method-icon.totp {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
}

.method-icon.passkey {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.method-icon.recovery {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.method-icon i {
  font-size: 1.125rem;
  color: white;
}

.method-details h5 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
  margin: 0 0 0.25rem;
}

.method-details p {
  font-size: 0.85rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
}

.method-status {
  flex-shrink: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.status-badge.enabled {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}

.status-badge.disabled {
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  color: var(--calid-text-secondary, #94a3b8);
}

.method-actions {
  flex-shrink: 0;
}

.action-btn {
  padding: 0.625rem 1.25rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.action-btn.primary {
  background: #00d4ff;
  color: #0a0a0f;
  font-weight: 600;
}

.action-btn.primary:hover:not(:disabled) {
  background: #00b8e0;
}

.action-btn.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn.secondary {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--calid-text-primary, #f0f0f5);
}

.action-btn.secondary:hover {
  background: rgba(255, 255, 255, 0.1);
}

.action-btn.danger {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.action-btn.danger:hover {
  background: rgba(239, 68, 68, 0.25);
}

.passkey-list {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
}

.passkey-list h6 {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.passkey-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.06));
  border-radius: 10px;
  margin-bottom: 0.75rem;
}

.passkey-item:last-child {
  margin-bottom: 0;
}

.passkey-icon {
  width: 40px;
  height: 40px;
  background: rgba(16, 185, 129, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #34d399;
}

.passkey-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.passkey-name {
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
}

.passkey-meta {
  font-size: 0.8rem;
  color: var(--calid-text-secondary, #94a3b8);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.passkey-meta i {
  font-size: 0.7rem;
  margin-right: 0.25rem;
}

.passkey-delete {
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.passkey-delete:hover {
  background: rgba(239, 68, 68, 0.2);
}

.browser-warning {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  color: #fbbf24;
  border-radius: 10px;
  font-size: 0.9rem;
}

/* Disable TOTP Dialog */
.disable-totp-dialog {
  text-align: center;
  padding: 1rem 0;
}

.warning-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1.25rem;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.warning-icon i {
  font-size: 1.75rem;
  color: #ef4444;
}

.disable-totp-dialog p {
  color: var(--calid-text-secondary, #94a3b8);
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
}

.disable-code-input {
  width: 100%;
  padding: 1rem;
  font-size: 1.75rem;
  text-align: center;
  letter-spacing: 0.75rem;
  border: 2px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 12px;
  outline: none;
  transition: all 0.2s ease;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  color: var(--calid-text-primary, #f0f0f5);
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
}

.disable-code-input:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15);
}

.disable-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.btn-cancel,
.btn-danger {
  flex: 1;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-cancel {
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  color: var(--calid-text-secondary, #94a3b8);
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.1);
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Dialog overrides for dark mode */
:deep(.security-dialog .p-dialog) {
  background: var(--calid-card-bg, #111827);
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 20px;
}

:deep(.security-dialog.danger .p-dialog) {
  border-color: rgba(239, 68, 68, 0.2);
}

:deep(.security-dialog .p-dialog-header) {
  background: transparent;
  border-bottom: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  padding: 1.25rem 1.5rem;
  color: var(--calid-text-primary, #f0f0f5);
}

:deep(.security-dialog .p-dialog-header .p-dialog-title) {
  font-weight: 600;
  font-size: 1.125rem;
}

:deep(.security-dialog .p-dialog-header-icons) {
  gap: 0.5rem;
}

:deep(.security-dialog .p-dialog-header-icon) {
  color: var(--calid-text-secondary, #94a3b8);
  width: 2rem;
  height: 2rem;
}

:deep(.security-dialog .p-dialog-header-icon:hover) {
  background: rgba(255, 255, 255, 0.05);
}

:deep(.security-dialog .p-dialog-content) {
  background: transparent;
  padding: 1.5rem !important;
}

/* Light mode styles */
:root:not(.dark-version) .security-section {
  background: white;
  border-color: #e5e7eb;
}

:root:not(.dark-version) .section-header {
  border-bottom-color: #f3f4f6;
}

:root:not(.dark-version) .section-info h4,
:root:not(.dark-version) .method-details h5 {
  color: #1f2937;
}

:root:not(.dark-version) .section-info p,
:root:not(.dark-version) .method-details p {
  color: #6b7280;
}

:root:not(.dark-version) .security-method {
  background: #f9fafb;
  border-color: #e5e7eb;
}

:root:not(.dark-version) .status-badge.disabled {
  background: #f3f4f6;
  color: #6b7280;
}

:root:not(.dark-version) .passkey-item {
  background: white;
  border-color: #e5e7eb;
}

:root:not(.dark-version) .passkey-name {
  color: #1f2937;
}

:root:not(.dark-version) .passkey-meta {
  color: #9ca3af;
}

:root:not(.dark-version) .disable-code-input {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #1f2937;
}

:root:not(.dark-version) .btn-cancel {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #374151;
}

:root:not(.dark-version) .btn-cancel:hover {
  background: #e5e7eb;
}

:root:not(.dark-version) .action-btn.secondary {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #374151;
}

:root:not(.dark-version) .action-btn.secondary:hover {
  background: #e5e7eb;
}

:root:not(.dark-version) :deep(.security-dialog .p-dialog) {
  background: white;
  border-color: #e5e7eb;
}

:root:not(.dark-version) :deep(.security-dialog .p-dialog-header) {
  border-bottom-color: #f3f4f6;
  color: #1f2937;
}

/* Responsive */
@media (max-width: 768px) {
  .security-method {
    flex-wrap: wrap;
  }
  
  .method-status {
    order: 3;
    width: 100%;
    margin-top: 0.5rem;
  }
  
  .method-actions {
    order: 2;
  }
}
</style>
